"use client";

import Image from "next/image";
import Link from "next/link";
import { Menu } from "lucide-react";
import { useTranslations } from "next-intl";
import * as React from "react";

import { LanguageSwitcher } from "@/components/language-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export function LandingHeader({ locale }: { locale: string }) {
  const t = useTranslations();
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#features", label: t("landing.header.features") },
    { href: "#how-it-works", label: t("landing.header.howItWorks") },
  ];

  return (
    <header className={`sticky top-0 z-40 w-full border-b transition-[background-color,border-color] duration-300 ${scrolled ? "border-border/70 bg-background/75 backdrop-blur-xl" : "border-transparent bg-transparent"}`}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link href={`/${locale}`} className="flex items-center gap-2 font-semibold tracking-tight" aria-label={t("landing.header.homeLabel")}>
          <Image src="/logo.svg" alt="" width={28} height={28} className="size-7" priority />
          <span>MembEntry</span>
        </Link>
        <nav className="hidden items-center gap-7 md:flex" aria-label={t("landing.header.primaryNavigationLabel")}>
          {links.map((link) => <a key={link.href} href={link.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">{link.label}</a>)}
        </nav>
        <div className="hidden items-center gap-2 md:flex">
          <LanguageSwitcher />
          <ThemeToggle />
          <Button variant="ghost" size="sm" asChild className="active:scale-[0.97] motion-reduce:active:scale-100"><Link href={`/${locale}/auth`}>{t("landing.header.login")}</Link></Button>
          <Button size="sm" asChild className="active:scale-[0.97] motion-reduce:active:scale-100"><Link href={`/${locale}/auth/register`}>{t("landing.header.getStarted")}</Link></Button>
        </div>
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden active:scale-[0.97] motion-reduce:active:scale-100" aria-label={t("landing.header.openMenuLabel")}><Menu className="size-5" /></Button>
          </SheetTrigger>
          <SheetContent className="w-[min(90vw,22rem)]">
            <SheetHeader><SheetTitle className="flex items-center gap-2"><Image src="/logo.svg" alt="" width={24} height={24} />MembEntry</SheetTitle></SheetHeader>
            <nav className="flex flex-col gap-1 px-4" aria-label={t("landing.header.mobileNavigationLabel")}>
              {links.map((link) => <a key={link.href} href={link.href} className="rounded-lg px-3 py-3 text-muted-foreground hover:bg-muted hover:text-foreground">{link.label}</a>)}
              <div className="mt-3 flex items-center gap-2 border-t pt-4"><LanguageSwitcher /><ThemeToggle /></div>
              <Button variant="outline" asChild className="mt-3 active:scale-[0.97] motion-reduce:active:scale-100"><Link href={`/${locale}/auth`}>{t("landing.header.login")}</Link></Button>
              <Button asChild className="active:scale-[0.97] motion-reduce:active:scale-100"><Link href={`/${locale}/auth/register`}>{t("landing.header.getStarted")}</Link></Button>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
